<template>
  <div>
    <div class="flex items-center mb-5">
      <h3 class="uppercase text-lg font-semibold text-primary tracking-wide">
        Contract History
      </h3>
      <hr class="flex-1 border-primary mx-4" />
      <button
          @click="addContractRecord"
          class="flex items-center bg-green-200 border hover:bg-green-300 border-green-400 hover:border-green-500 hover:shadow active:shadow-inner transition duration-300 text-secondary rounded-md px-2 py-1"
      >
        <svg
            class="stroke-current h-5 w-5 mr-2"
            fill="none"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
            viewBox="0 0 24 24"
        >
          <path d="M12 5v14M5 12h14" />
        </svg>
        <span>Add Contract</span>
      </button>
    </div>

    <div class="flex flex-wrap w-full">
      <DataTable
          v-if="contracts && contracts.length > 0"
          class="w-full"
          :value="contracts"
          :rows="10"
          :paginator="true"
          :rowsPerPageOptions="$rowsPerPageOptions"
      >
        <Column
            v-for="col in columns"
            :key="col.field"
            :field="col.field"
            :header="col.header"
            :sortable="col.sortable"
            :headerStyle="{ width: col.width }"
        >
          <template v-slot:body="slotProps">
            <template v-if="col.slotName === 'start-slot'">
              <span>
                {{ slotProps.data.start_date ? formatDate(slotProps.data.start_date) : "N/A" }}
              </span>
            </template>

            <template v-else-if="col.slotName === 'end-slot'">
             <span>
               {{ slotProps.data.end_date ? formatDate(slotProps.data.end_date) : "N/A" }}
              </span>
            </template>

            <template v-else-if="col.slotName === 'action-slot'">
              <div class="flex space-x-4">
                <AppButton
                    color="blue"
                    label=""
                    :icon="true"
                    @click.native.stop="openEditContract(slotProps)"
                >
                  <template v-slot:icon>
                    <Icon iconType="edit" />
                  </template>
                </AppButton>
                <AppButton
                    color="red"
                    label=""
                    :icon="true"
                    @click.native.stop="confirmDeleteContract(slotProps)"
                >
                  <template v-slot:icon>
                    <Icon iconType="delete" />
                  </template>
                </AppButton>
              </div>
            </template>

            <template v-else>
              {{ slotProps.data[col.field] || "N/A" }}
            </template>
          </template>
        </Column>

        <template #empty>
          <p class="mx-auto my-16">There are no contract records available.</p>
        </template>
      </DataTable>
    </div>

    <portal to="overlay-outlet">
      <panel
          :showing="addEditContractPanelOpen"
          @close="closeAddEditContract"
          :title="editingContract.contract_id ? 'Edit Contract' : 'Add Contract'"
      >
        <AddContractRecord
            :contract_id="selectedContractId"
            :initial_contract="selectedContract"
            :equipment_id="equipment_id"
            @refreshEquipment="onContractSaved"
        />
      </panel>
    </portal>

    <portal to="overlay-outlet">
      <confirm-alert
          :showingConfirm="confirmAlert.showing"
          :message="confirmAlert.message"
          @complete="confirmAlert.resultFunction"
      />
    </portal>
  </div>
</template>

<script>
import Icon from "@/components/Icon.vue";
import AppButton from "@/components/Button.vue";
import notificationMixin from "@/mixins/notificationMixin";

const Notification = () => import("@/components/Notification.vue");
const ConfirmAlert = () => import("@/components/ConfirmAlert.vue");
const Panel = () => import("@/components/Panel.vue");
const AddContractRecord = () => import("@/components/Equipment/AddContract.vue");

export default {
  mixins: [notificationMixin],
  name: "ContractHistory",
  components: {
    AppButton,
    Icon,
    Notification,
    ConfirmAlert,
    Panel,
    AddContractRecord,
  },
  props: {
    contracts: Array,
    equipment_id: String,
  },
  data() {
    return {
      addEditContractPanelOpen: false,
      editingContract: {},
      confirmAlert: {
        resultFunction: "",
        showing: false,
        message: "",
        title: "",
        data: "",
      },
      columns: [
        {field: "start_date", header: "Start Date", sortable: true, width: "20%", slotName: "start-slot"},
        {field: "end_date", header: "End Date", sortable: true, width: "20%", slotName: "end-slot"},
        {field: "contract_type", header: "Contract Type", sortable: true, width: "20%"},
        {field: "payment_type", header: "Payment Type", sortable: true, width: "20%"},
        {
          field: "action",
          header: "",
          sortable: false,
          width: "10%",
          custom: true,
          slotName: "action-slot",
        },
      ],
      selectedContractId: "",
      selectedContract: null,
    };
  },
  methods: {
    formatDate(unixTimestamp) {
      return this.$moment.unix(unixTimestamp).format("DD-MM-YYYY");
    },

    addContractRecord() {
      this.editingContract = {};
      this.selectedContractId = "";
      this.selectedContract = null;
      this.addEditContractPanelOpen = true;
    },

    async onContractSaved() {
      this.closeAddEditContract();
      await this.refreshEquipment();
    },

    closeAddEditContract() {
      this.addEditContractPanelOpen = false;
    },

    async refreshEquipment() {
      this.$emit("refreshEquipment");
    },

    openEditContract(selectedRow) {
      if (selectedRow && selectedRow.data && selectedRow.data.contract_id) {
        this.selectedContractId = selectedRow.data.contract_id;
        this.selectedContract = selectedRow.data;
        this.addEditContractPanelOpen = true;
      }
    },

    async confirmDeleteContract(selectedRow) {
      if (!selectedRow || !selectedRow.data || !selectedRow.data.contract_id) return;

      const contractId = selectedRow.data.contract_id;
      const confirmation = await this.confirmAction({
        message: "Do you want to delete this contract?",
        header: "Confirm Action",
        acceptLabel: "Yes",
        rejectLabel: "No"
      });

      if (confirmation) {
        try {
          if (contractId) {
            await this.EquipmentService.deleteContract(this.equipment_id, contractId);
            this.notifySuccess("Contract deleted successfully");
            await this.refreshEquipment();
          } else {
            this.notifyError("Contract ID is missing");
          }
        } catch (error) {
          this.notifyError("Couldn't delete the contract");
          console.error(error);
        }
      }
    },
  },
};
</script>
